import { format } from "date-fns";

import { IFoundTrain, IMenuRaw, toFakeUtc } from "@framework/provider-asu";

export * from "./alarms";
export * from "./number";
export * from "./use-image-size";

export { toFakeUtc };

export const capitalize = (str: string): string => `${str[0].toUpperCase()}${str.slice(1, str.length)}`;

export enum DATE_POSTFIXES {
  start = "T00:00:00.000Z",
  end = "T23:59:59.999Z",
}

export enum SELECTED_ITEM_TYPE {
  train = "train",
  station = "station",
}

export const getSelectedItemType = (selectedItem: IMenuRaw | IFoundTrain | null): SELECTED_ITEM_TYPE | null => {
  if (!selectedItem) {
    return null;
  }

  return "TrainId" in selectedItem ? SELECTED_ITEM_TYPE.train : SELECTED_ITEM_TYPE.station;
};

export interface IToISOStringOptions {
  customPostfix?: string;
  postfix?: keyof typeof DATE_POSTFIXES;
}

export const toISOStringJournal = (
  date: string | Date,
  { postfix = "start", customPostfix }: IToISOStringOptions = { postfix: "start" },
): string => {
  const datePostfix = customPostfix || DATE_POSTFIXES[postfix];
  return `${format(new Date(date), "yyyy-MM-dd")}${datePostfix}`;
};

export const parseTrainColour = (num: number) => {
  const b = num >> 16;
  const g = (num >> 8) & 0xff;
  const r = num & 0xff;

  return {
    color: `rgb(${r}, ${g}, ${b})`,
    backgroundColor: "#CCC",
  };
};

/**
 * Splits a UTC date into date/time components.
 */
export const getDateComponents = (date: Date) => {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hours: date.getUTCHours(),
    minutes: date.getUTCMinutes(),
    seconds: date.getUTCSeconds(),
    milliseconds: date.getUTCMilliseconds(),
  };
};

/**
 * Formats a UTC date into DD.MM.YYYY HH:MM, optionally with seconds.
 */
export const formatDateHumanReadable = (date: Date | string, options?: { withSeconds?: boolean }): string => {
  const dt = typeof date === "string" ? new Date(date) : date;
  const value = options?.withSeconds ? new Date(Math.round(dt.getTime() / 1000) * 1000) : dt;
  const { day, month, year, hours, minutes, seconds } = getDateComponents(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = options?.withSeconds ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}`;

  return `${pad(day)}.${pad(month)}.${year} ${time}`;
};

/**
 * Formats a UTC date for trend tooltips as DD.MM.YYYY HH:MM:SS, rounding to the nearest second
 */
export const formatTrendTooltipDate = (date: Date | string): string => {
  const dt = typeof date === "string" ? new Date(date) : date;
  const rounded = new Date(Math.round(dt.getTime() / 1000) * 1000);
  const { day, month, year, hours, minutes, seconds } = getDateComponents(rounded);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(day)}.${pad(month)}.${year} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

// Maps Delphi/C++ Builder colors to CSS hex values.
const colorMap: Record<string, string> = {
  clBlack: "#000000",
  clMaroon: "#800000",
  clRed: "#FF0000",
  clFuchsia: "#FF00FF",
  clOlive: "#808000",
  clYellow: "#FFFF00",
  clGreen: "#008000",
  clLime: "#00FF00",
  clTeal: "#008080",
  clAqua: "#00FFFF",
  clNavy: "#000080",
  clBlue: "#0000FF",
  clPurple: "#800080",
  clGray: "#808080",
  clSilver: "#C0C0C0",
  clWhite: "#FFFFFF",
};

const decimalColorPattern = /^-?\d+$/;

const parseDecimalColor = (value: string): string => {
  const decimalValue = Number(value);
  const normalized = decimalValue >>> 0;
  const red = normalized & 0xff;
  const green = (normalized >> 8) & 0xff;
  const blue = (normalized >> 16) & 0xff;

  return `#${red.toString(16).padStart(2, "0")}${green.toString(16).padStart(2, "0")}${blue
    .toString(16)
    .padStart(2, "0")}`;
};

export const isSupportedColorValue = (color: string): boolean => {
  const normalizedColor = color?.trim();

  if (!normalizedColor) return false;
  if (colorMap[normalizedColor]) return true;
  if (/^[0-9a-fA-F]{8}$/.test(normalizedColor)) return true;
  if (/^#?[0-9a-fA-F]{6}$/.test(normalizedColor)) return true;

  return decimalColorPattern.test(normalizedColor);
};

export const parseColor = (color: string): string => {
  const normalizedColor = color?.trim();

  if (!normalizedColor) return "#000000";
  if (colorMap[normalizedColor]) return colorMap[normalizedColor];

  if (decimalColorPattern.test(normalizedColor)) {
    return parseDecimalColor(normalizedColor);
  }

  if (/^[0-9a-fA-F]{8}$/.test(normalizedColor)) {
    const rr = normalizedColor.slice(6, 8);
    const gg = normalizedColor.slice(4, 6);
    const bb = normalizedColor.slice(2, 4);
    return `#${rr}${gg}${bb}`;
  }
  if (/^#?[0-9a-fA-F]{6}$/.test(normalizedColor)) {
    return normalizedColor.startsWith("#") ? normalizedColor : `#${normalizedColor}`;
  }

  return "#000000";
};
